import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useGetStatementQualityReport } from '../../api/statementqualityreport.js';
import { Container, Skeleton, Box } from '@mui/material';
import { formatAccountDisplayName } from '../../utils/scanReportUtils';
import { formatMonthShort } from '../../utils/formatters';
import styles from './report-statement-quality-heatmap.module.css';
import { orderQualityAccounts, qualityGroupLabel, qualityGroupLabelFitted } from './report-statement-quality-order.js';

/*
 * Coverage matrix: accounts down the left, calendar months across the top.
 *
 * This used to be a single SVG that shrink-scaled to fit its container
 * (scale = min(1, containerWidth / viewBoxWidth)), which meant every label got
 * smaller as the case's date range got longer — a 60-month case rendered
 * account names at ~5px. It now renders at 1:1 always, with the account-name
 * gutter frozen and the month grid scrolling horizontally (Trello RI8lx3jW).
 *
 * The horizontal freeze is a flex split into two side-by-side SVGs rather than
 * a sticky column, because CSS positioning does not apply to elements inside
 * an SVG. The two body SVGs stay row-aligned by sharing the layoutRows
 * computation and bodyHeight.
 *
 * The month/year axis is a third SVG in a sticky strip above them, so it pins
 * under the top bar while the accounts scroll past (Trello jcgwB1Yb). It has
 * to sit outside .gridScroll to escape that element's scrollport, so its
 * horizontal position is mirrored from the grid in syncScrollState.
 */

// Geometry. GUTTER_WIDTH should not drop below ~260 — formatAccountDisplayName
// emits strings like "*1111 Chase Checking" (~145px at 13px DM Sans).
const GUTTER_WIDTH = 300;
const HEADER_H = 60;          // height of the sticky year + month axis strip
const CELL_W = 30;
const ROW_H = 30;
const GROUP_H = 26;
const EMPTY_SET = new Set();
const GRID_PAD_RIGHT = 2;     // keeps the last cell's border stroke off the edge
const BOTTOM_PAD = 14;        // >= 12: year separators overrun the grid by 10px

// Quality status → CSS class. These are classes rather than fill="var(--token)"
// attributes because var() is not valid in an SVG presentation attribute.
const STATUS_CLASS = {
  OK: styles.cellOk,
  MISSING: styles.cellMissing,
  HAS_WARNINGS: styles.cellWarning,   // informational (out-of-bounds txns, inferred dates)
  DEFAULT: styles.cellReview,         // balance mismatches, needs review
};

const parseDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getYearMonth = (dateStr) => dateStr.substring(0, 7); // YYYY-MM

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

// What portion of a month is covered by a date range, as 0..1 fractions.
const getMonthCoverage = (startDate, endDate, targetMonth) => {
  const [targetYear, targetMonthNum] = targetMonth.split('-').map(Number);
  const monthStart = new Date(targetYear, targetMonthNum - 1, 1);
  const monthEnd = new Date(targetYear, targetMonthNum - 1, getDaysInMonth(targetYear, targetMonthNum));

  const rangeStart = parseDate(startDate);
  const rangeEnd = parseDate(endDate);

  const overlapStart = new Date(Math.max(rangeStart.getTime(), monthStart.getTime()));
  const overlapEnd = new Date(Math.min(rangeEnd.getTime(), monthEnd.getTime()));

  if (overlapStart > overlapEnd) return null;

  const totalDaysInMonth = getDaysInMonth(targetYear, targetMonthNum);
  const startFraction = (overlapStart.getDate() - 1) / totalDaysInMonth;
  const endFraction = overlapEnd.getDate() / totalDaysInMonth;

  return { startFraction, endFraction, coverage: endFraction - startFraction };
};

// Build the month axis and account list from the report payload.
// Exported for the PDF export, so the printed matrix derives its month axis
// from exactly the same logic as the on-screen one.
export const processData = (data, visibleYears = null) => {
  const accountIds = Object.keys(data);

  const allDates = [];
  accountIds.forEach((accountId) => {
    const statements = data[accountId].combined_statements;
    if (!statements || statements.length === 0) return;
    statements.forEach((statement) => {
      if (statement.end_date) allDates.push(statement.end_date);
      if (statement.begin_date) allDates.push(statement.begin_date);
      // Missing-statement placeholders carry expected_* dates instead.
      if (statement.expected_begin_date) allDates.push(statement.expected_begin_date);
      if (statement.expected_end_date) allDates.push(statement.expected_end_date);
    });
  });

  allDates.sort();

  const minDate = allDates.length > 0 ? parseDate(allDates[0]) : new Date();
  const maxDate = allDates.length > 0 ? parseDate(allDates[allDates.length - 1]) : new Date();

  // visibleYears narrows the axis (the report's year control) without
  // touching coverage: a statement straddling a year boundary still paints
  // whichever of its months remain visible.
  const yearSet = visibleYears ? new Set(visibleYears) : null;

  const months = [];
  const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  while (cursor <= maxDate) {
    const year = String(cursor.getFullYear());
    if (!yearSet || yearSet.has(year)) {
      months.push(`${year}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return { accountIds, months };
};

// Coverage segments for one (account, month) cell, merged where adjacent
// segments share a status. Exported alongside processData for the PDF export.
export const getMonthCoverageData = (data, accountId, month) => {
  const statements = data[accountId].combined_statements;
  if (!statements) return [];

  const coverageSegments = [];

  statements.forEach((statement) => {
    // Missing-statement placeholders contribute no coverage.
    if (statement.missing === true) return;
    if (!statement.end_date) return;

    let startDate;
    let endDate;

    if (statement.begin_date && statement.end_date) {
      startDate = statement.begin_date;
      endDate = statement.end_date;
    } else {
      // Fallback: only the exact end_date month, don't assume full coverage.
      if (getYearMonth(statement.end_date) !== month) return;
      const endDateObj = parseDate(statement.end_date);
      const year = endDateObj.getFullYear();
      const monthNum = endDateObj.getMonth() + 1;
      const lastDay = getDaysInMonth(year, monthNum);
      startDate = `${year}-${String(monthNum).padStart(2, '0')}-01`;
      endDate = `${year}-${String(monthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    }

    const coverage = getMonthCoverage(startDate, endDate, month);
    if (!coverage) return;

    coverageSegments.push({
      startFraction: coverage.startFraction,
      endFraction: coverage.endFraction,
      className: STATUS_CLASS[statement.quality_status] || STATUS_CLASS.DEFAULT,
      quality_status: statement.quality_status,
    });
  });

  coverageSegments.sort((a, b) => a.startFraction - b.startFraction);

  const mergedSegments = [];
  coverageSegments.forEach((segment) => {
    const lastSegment = mergedSegments[mergedSegments.length - 1];
    if (lastSegment
        && lastSegment.className === segment.className
        && lastSegment.endFraction >= segment.startFraction) {
      lastSegment.endFraction = Math.max(lastSegment.endFraction, segment.endFraction);
    } else {
      mergedSegments.push({ ...segment });
    }
  });

  return mergedSegments;
};

// _displayName is no longer stored (computed on demand); the quality report
// serves raw scanned-account fields, so build the name from them via the
// shared formatter (nickname / last4 / bank / type).
const getAccountName = (data, accountId) => (
  formatAccountDisplayName(data[accountId].bank_account) || `Account ${accountId}`
);

/*
 * The gutter is a fixed width, and SVG text neither wraps nor ellipsises — a
 * long name simply runs past the viewBox and is clipped, which silently turns
 * "Apple Card Christopher Held (…)" into "ple Card Christopher Held (…)". That
 * reads as the account's actual name, so it is worse than an obvious cut.
 *
 * Truncated from the end because the identifying part — the last four, or the
 * card holder — leads. The full name always stays available as a <title>.
 * Character-based rather than measured: getComputedTextLength needs a laid-out
 * node, and 13px DM Sans averages ~6.2px per character across roughly 280px of
 * usable gutter.
 */
const MAX_LABEL_CHARS = 42;

export const truncateAccountLabel = (label) => (
  label.length > MAX_LABEL_CHARS ? `${label.slice(0, MAX_LABEL_CHARS - 1).trimEnd()}…` : label
);

// Group band labels ("<owner> · <type>", plus "(N hidden)" when collapsed) are
// measured as rendered and shortened only when they truly overflow the gutter:
// the owner part takes the "…", the account type always stays whole. Where the
// browser cannot measure (tests), a character budget stands in.
const GROUP_LABEL_PAD_RIGHT = 8;
const FALLBACK_GROUP_LABEL_CHARS = 44;

const GroupBandLabel = ({ entry, suffix, x, y, bandKey, onShortened }) => {
  const ref = useRef(null);
  const full = qualityGroupLabel(entry) + suffix;
  const [shown, setShown] = useState(full);

  useLayoutEffect(() => {
    const el = ref.current;
    const maxWidth = GUTTER_WIDTH - x - GROUP_LABEL_PAD_RIGHT;
    let next = full;
    if (el && typeof el.getComputedTextLength === 'function') {
      const fits = (candidate) => {
        el.textContent = candidate;
        return el.getComputedTextLength() <= maxWidth;
      };
      if (!fits(full) && entry.assignment) {
        // Longest owner prefix that fits, by binary search on its length.
        let lo = 1;
        let hi = entry.assignment.label.length - 1;
        next = qualityGroupLabelFitted(entry, 1) + suffix;
        while (lo <= hi) {
          const mid = Math.floor((lo + hi) / 2);
          const candidate = qualityGroupLabelFitted(entry, mid + 1 + qualityGroupLabel(entry).length
            - entry.assignment.label.length) + suffix;
          if (fits(candidate)) { next = candidate; lo = mid + 1; } else { hi = mid - 1; }
        }
      }
      el.textContent = next;
    } else if (full.length > FALLBACK_GROUP_LABEL_CHARS) {
      next = qualityGroupLabelFitted(entry, FALLBACK_GROUP_LABEL_CHARS - suffix.length) + suffix;
    }
    setShown(next);
    onShortened(bandKey, next !== full);
    // `entry` is rebuilt every render; `full` already captures its owner + type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, x, suffix, bandKey, onShortened]);

  return (
    <text
      ref={ref}
      x={x}
      y={y}
      textAnchor="start"
      dominantBaseline="middle"
      className={styles.groupLabel}
    >
      {shown}
    </text>
  );
};

/*
 * The chart itself. Split out from BankStatementQualityHeatmap because that
 * component early-returns for loading / error / empty before the month axis
 * exists, so scroll-state hooks have nowhere legal to live there.
 */
const QualityTimeline = ({ data, visibleYears, collapsedGroups, onToggleGroup, stickyOffset, onStickyTopChange }) => {
  const scrollRef = useRef(null);
  const headerRef = useRef(null);
  const stickyRef = useRef(null);
  const [scroll, setScroll] = useState({ scrolled: false, scrollable: false });
  const [stickyTop, setStickyTop] = useState(0);
  // Which group bands render a shortened label (they get a full-label tooltip).
  const [shortenedBands, setShortenedBands] = useState({});
  const markBandShortened = useCallback((key, shortened) => {
    setShortenedBands((prev) => (prev[key] === shortened ? prev : { ...prev, [key]: shortened }));
  }, []);

  /*
   * A sticky offset is measured from the scrollport's CONTENT box, so with
   * top: 0 the header pins below the scrollport's own padding — and rows are
   * visible in that band as they scroll past above it. Pull the header up by
   * that padding so it pins flush. CSS cannot read an ancestor's padding, and
   * hardcoding AppShell's --space-6 here would silently break if that padding
   * ever changes, so measure the actual scrollport once.
   */
  useEffect(() => {
    let node = stickyRef.current?.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        const padding = parseFloat(window.getComputedStyle(node).paddingTop);
        const top = Number.isFinite(padding) ? -padding : 0;
        setStickyTop(top);
        // Anything else pinning above this (the report's filter bar) has to
        // share the same base, or the two stack against different origins and
        // the axis ends up hidden behind the bar.
        if (onStickyTopChange) onStickyTopChange(top);
        return;
      }
      node = node.parentElement;
    }
    setStickyTop(0);
    if (onStickyTopChange) onStickyTopChange(0);
  }, [onStickyTopChange]);

  const syncScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // The pinned header is a separate scroll container (it has to be — see
    // .stickyHeader in the CSS), so its horizontal position is mirrored from
    // the grid rather than shared. Done here, not in onScroll, so keyboard and
    // programmatic scrolling stay in register too — assigning scrollLeft does
    // not fire a scroll event under jsdom.
    if (headerRef.current) headerRef.current.scrollLeft = el.scrollLeft;
    setScroll({
      scrolled: el.scrollLeft > 0,
      scrollable: el.scrollWidth > el.clientWidth + 1,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    syncScrollState();
    const observer = new ResizeObserver(syncScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [syncScrollState]);

  // Browsers scroll a focused overflow container with the arrow keys on their
  // own, but this is the only way a keyboard-only user can reach the
  // off-screen months (WCAG 2.1.1), so it is handled explicitly rather than
  // left to a default we cannot assert on.
  const handleKeyDown = useCallback((event) => {
    const el = scrollRef.current;
    if (!el) return;
    const page = Math.max(el.clientWidth - CELL_W, CELL_W);
    const deltas = {
      ArrowRight: CELL_W,
      ArrowLeft: -CELL_W,
      PageDown: page,
      PageUp: -page,
    };
    if (event.key === 'Home' || event.key === 'End') {
      el.scrollLeft = event.key === 'Home' ? 0 : el.scrollWidth;
    } else if (deltas[event.key] !== undefined) {
      el.scrollLeft += deltas[event.key];
    } else {
      return;
    }
    event.preventDefault();
    syncScrollState();
  }, [syncScrollState]);

  const { accountIds, months } = processData(data, visibleYears);

  // Order by the backend-assigned sort_index (canonical account-type grouping)
  // rather than object-key order -- split into Personal / per-entity sections
  // when any account is entity-titled -- inserting a group-header row wherever
  // the group changes so rows are grouped like the detail list below.
  const accountIdSet = new Set(accountIds);
  const orderedAccounts = orderQualityAccounts(data).filter((a) => accountIdSet.has(a.accountId));

  // A collapsed group keeps its band — that is what you click to reopen it —
  // but contributes no account rows, which is the point: one click removes a
  // 16-account group from the chart. Collapse is a reading aid only and never
  // reaches the PDF; the export is driven by the filtered payload, not by this.
  const collapsed = collapsedGroups || EMPTY_SET;
  const layoutRows = [];
  let runningY = 0;
  let prevGroupKey = null;
  let accountIndex = 0;
  orderedAccounts.forEach((entry) => {
    const { accountId, group, groupKey } = entry;
    if (group && groupKey !== prevGroupKey) {
      const isCollapsed = collapsed.has(groupKey);
      const memberCount = orderedAccounts.filter((a) => a.groupKey === groupKey).length;
      layoutRows.push({
        type: 'header',
        key: groupKey,
        label: qualityGroupLabel(entry),
        entry,
        suffix: isCollapsed ? ` (${memberCount} hidden)` : '',
        y: runningY,
        collapsed: isCollapsed,
        memberCount,
      });
      runningY += GROUP_H;
      prevGroupKey = groupKey;
    }
    if (groupKey && collapsed.has(groupKey)) return;
    layoutRows.push({ type: 'account', accountId, y: runningY, index: accountIndex });
    accountIndex += 1;
    runningY += ROW_H;
  });

  const gridHeight = runningY;
  const accountRows = layoutRows.filter((r) => r.type === 'account');
  const headerRows = layoutRows.filter((r) => r.type === 'header');

  const gridWidth = (months.length * CELL_W) + GRID_PAD_RIGHT;
  // The header band is its own SVG in the sticky strip, so body coordinates
  // start at 0 rather than being offset by HEADER_H.
  const bodyHeight = gridHeight + BOTTOM_PAD;

  const rowBandClass = (row) => (row.index % 2 === 1 ? styles.rowBandAlt : styles.rowBand);

  // Group months by year for the year header band.
  const yearGroups = {};
  months.forEach((month) => {
    const year = month.split('-')[0];
    if (!yearGroups[year]) yearGroups[year] = [];
    yearGroups[year].push(month);
  });

  // x positions of each year boundary after the first.
  const yearSeparators = Object.keys(yearGroups)
    .sort()
    .slice(1)
    .map((year) => months.indexOf(yearGroups[year][0]) * CELL_W);

  const rangeLabel = months.length > 0
    ? `${formatMonthShort(months[0])} ${months[0].split('-')[0]} to `
      + `${formatMonthShort(months[months.length - 1])} ${months[months.length - 1].split('-')[0]}`
    : 'no months';

  return (
    <>
      {/* Month/year axis, lifted out of the grid SVG so it can pin to the page
          scrollport while the accounts scroll underneath it (Trello jcgwB1Yb). */}
      <div
        className={styles.stickyHeader}
        ref={stickyRef}
        style={{ top: stickyTop + (stickyOffset || 0) }}
        data-testid="qh-header"
      >
        <div className={`${styles.headerCorner} ${scroll.scrolled ? styles.gutterScrolled : ''}`}>
          <svg
            className={styles.svgBlock}
            width={GUTTER_WIDTH}
            height={HEADER_H}
            viewBox={`0 0 ${GUTTER_WIDTH} ${HEADER_H}`}
            aria-hidden="true"
          />
        </div>

        <div className={styles.headerViewport} ref={headerRef} data-testid="qh-header-viewport">
          <svg
            className={styles.svgBlock}
            width={gridWidth}
            height={HEADER_H}
            viewBox={`0 0 ${gridWidth} ${HEADER_H}`}
          >
            <title>Month and year axis</title>

            {/* Year headers */}
            {Object.keys(yearGroups).map((year) => {
              const startIndex = months.indexOf(yearGroups[year][0]);
              const endIndex = months.indexOf(yearGroups[year][yearGroups[year].length - 1]);
              const centerX = (startIndex * CELL_W) + (((endIndex - startIndex + 1) * CELL_W) / 2);
              return (
                <text
                  key={`year-${year}`}
                  x={centerX}
                  y={25}
                  textAnchor="middle"
                  className={styles.yearLabel}
                >
                  {year}
                </text>
              );
            })}

            {/* Month labels */}
            {months.map((month, index) => (
              <text
                key={`month-${month}`}
                x={(index * CELL_W) + (CELL_W / 2)}
                y={45}
                textAnchor="middle"
                className={styles.monthLabel}
              >
                {formatMonthShort(month)}
              </text>
            ))}

            {/* Year separator stubs, so the boundary line reads continuous
                into the body while the header is inline. */}
            {yearSeparators.map((x, index) => (
              <line
                key={`header-separator-${index}`}
                x1={x}
                y1={15}
                x2={x}
                y2={HEADER_H}
                className={styles.yearSeparator}
                strokeWidth="2"
                shapeRendering="crispEdges"
              />
            ))}
          </svg>
        </div>
      </div>

      <div className={styles.chartRow}>
        {/* Frozen account-name gutter — deliberately outside the scroller. */}
        <div
          className={`${styles.gutter} ${scroll.scrolled ? styles.gutterScrolled : ''}`}
          data-testid="qh-gutter"
        >
          <svg
            className={styles.svgBlock}
            width={GUTTER_WIDTH}
            height={bodyHeight}
            viewBox={`0 0 ${GUTTER_WIDTH} ${bodyHeight}`}
          >
            <title>Account names</title>

            {/* Row banding, so the eye can track a name across the grid. */}
            {accountRows.map((row) => (
              <rect
                key={`gutter-band-${row.accountId}`}
                x={0}
                y={row.y}
                width={GUTTER_WIDTH}
                height={ROW_H}
                className={rowBandClass(row)}
                shapeRendering="crispEdges"
              />
            ))}

            {/* Group bands. The band rect is drawn in both SVGs at the same y
                so it reads as one continuous stripe across the freeze line;
                the label lives only here. */}
            {headerRows.map((row) => (
              <g
                key={`group-${row.label}-${row.y}`}
                className={onToggleGroup ? styles.groupToggle : undefined}
                role={onToggleGroup ? 'button' : undefined}
                tabIndex={onToggleGroup ? 0 : undefined}
                aria-expanded={onToggleGroup ? !row.collapsed : undefined}
                aria-label={onToggleGroup
                  ? `${row.label}, ${row.memberCount} accounts, ${row.collapsed ? 'collapsed' : 'expanded'}`
                  : undefined}
                onClick={onToggleGroup ? () => onToggleGroup(row.key) : undefined}
                onKeyDown={onToggleGroup ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onToggleGroup(row.key);
                  }
                } : undefined}
                data-testid={`qh-group-${row.key}`}
              >
                {/* Full label on hover whenever the band shows a shortened one. */}
                {shortenedBands[row.key] ? <title>{row.label}</title> : null}
                <rect
                  x={0}
                  y={row.y}
                  width={GUTTER_WIDTH}
                  height={GROUP_H}
                  className={styles.groupBand}
                  shapeRendering="crispEdges"
                />
                {onToggleGroup && (
                  // Solid triangle: a chevron glyph would be a font dependency,
                  // and this reads at 10px where a text arrow does not.
                  <path
                    className={styles.groupCaret}
                    transform={`translate(9, ${row.y + (GROUP_H / 2)})`}
                    d={row.collapsed ? 'M -3 -4 L 4 0 L -3 4 Z' : 'M -4 -3 L 4 -3 L 0 4 Z'}
                  />
                )}
                <GroupBandLabel
                  entry={row.entry}
                  suffix={row.suffix}
                  x={onToggleGroup ? 22 : 8}
                  y={row.y + (GROUP_H / 2)}
                  bandKey={row.key}
                  onShortened={markBandShortened}
                />
              </g>
            ))}

            {accountRows.map((row) => {
              const fullName = getAccountName(data, row.accountId);
              const shown = truncateAccountLabel(fullName);
              return (
                <text
                  key={`account-${row.accountId}`}
                  x={GUTTER_WIDTH - 12}
                  y={row.y + (ROW_H / 2)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className={styles.accountLabel}
                >
                  {shown !== fullName && <title>{fullName}</title>}
                  {shown}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Scrolling month grid. tabIndex is required for WCAG 2.1.1 — without
            it a keyboard-only user cannot reach the off-screen months. */}
        <div
          className={styles.gridScroll}
          ref={scrollRef}
          onScroll={syncScrollState}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="region"
          aria-label={`Statement coverage grid, ${months.length} months, ${rangeLabel}. Scroll horizontally to see all months.`}
          data-testid="qh-scroll"
        >
          <svg
            className={styles.svgBlock}
            width={gridWidth}
            height={bodyHeight}
            viewBox={`0 0 ${gridWidth} ${bodyHeight}`}
          >
            <title>Monthly statement coverage</title>

            {/* Row banding — must match the gutter's banding exactly. */}
            {accountRows.map((row) => (
              <rect
                key={`grid-band-${row.accountId}`}
                x={0}
                y={row.y}
                width={gridWidth}
                height={ROW_H}
                className={rowBandClass(row)}
                shapeRendering="crispEdges"
              />
            ))}

            {/* Group bands (label-less half of the continuous stripe) */}
            {headerRows.map((row) => (
              <rect
                key={`grid-group-${row.label}-${row.y}`}
                x={0}
                y={row.y}
                width={gridWidth}
                height={GROUP_H}
                className={styles.groupBand}
                shapeRendering="crispEdges"
              />
            ))}

            {/* Coverage cells */}
            {accountRows.map((row) => (
              months.map((month, monthIndex) => {
                const x = monthIndex * CELL_W;
                const y = row.y;
                const coverageSegments = getMonthCoverageData(data, row.accountId, month);

                return (
                  <g key={`cell-${row.accountId}-${month}`}>
                    {/* Coverage segments paint over the row band; an uncovered
                        cell simply shows the band, which is the "missing" read. */}
                    {coverageSegments.map((segment, segmentIndex) => (
                      <rect
                        key={`segment-${segmentIndex}`}
                        x={x + (segment.startFraction * CELL_W)}
                        y={y}
                        width={(segment.endFraction - segment.startFraction) * CELL_W}
                        height={ROW_H}
                        className={segment.className}
                        stroke="none"
                      />
                    ))}

                    <rect
                      x={x}
                      y={y}
                      width={CELL_W}
                      height={ROW_H}
                      className={styles.cellBorder}
                      strokeWidth="1"
                      shapeRendering="crispEdges"
                    />
                  </g>
                );
              })
            ))}

            {/* Year separator lines. Start at 0 — the y=15 inset belonged to
                the old in-SVG header band, which now lives in the sticky
                strip and draws its own stub. */}
            {yearSeparators.map((x, index) => (
              <line
                key={`separator-${index}`}
                x1={x}
                y1={0}
                x2={x}
                y2={gridHeight + 10}
                className={styles.yearSeparator}
                strokeWidth="2"
                shapeRendering="crispEdges"
              />
            ))}

            <line
              x1={0}
              y1={gridHeight}
              x2={months.length * CELL_W}
              y2={gridHeight}
              className={styles.gridBaseline}
              strokeWidth="1"
              shapeRendering="crispEdges"
            />
          </svg>
        </div>
      </div>

      {scroll.scrollable && (
        <p className={styles.scrollHint}>
          Scroll sideways to see all {months.length} months. Account names stay in place.
        </p>
      )}
    </>
  );
};

/*
 * `data` and `visibleYears` are optional. When the report renders this it
 * passes its filtered payload down, so chart and detail cannot disagree;
 * with no props the component still fetches for itself and stays usable
 * standalone. SWR dedupes the two calls on the shared cache key.
 */
const BankStatementQualityHeatmap = ({
  data: dataProp = null,
  visibleYears = null,
  collapsedGroups = null,
  onToggleGroup = null,
  stickyOffset = 0,
  onStickyTopChange = null,
  // Rendered inside the card immediately above the pinned month axis, so the
  // controls read as part of the table rather than as a detached page-width
  // band, and pin together with the axis as one unit.
  toolbar = null,
  // Overridable so a caller that narrows `data` itself (e.g. an active
  // filter) can explain the empty state accurately, rather than this
  // component's generic default reading as a second, unrelated problem.
  emptyMessage = 'No data available',
}) => {
  const missingStatementsResult = useGetStatementQualityReport();
  const data = dataProp ?? missingStatementsResult.data;

  // Loading state with Material UI Skeleton
  if (!dataProp && missingStatementsResult.isLoading) {
    return (
      <Container maxWidth="xl" sx={{ flexGrow: 1, padding: 2 }}>
        <div className={styles.card}>
          {/* Mirrors the loaded header's shape — title left, key right — so the
              card does not jump when the data arrives. */}
          <div className={styles.cardHeader}>
            <Skeleton variant="text" width={180} height={28} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="rectangular" width={70} height={16} />
              <Skeleton variant="rectangular" width={70} height={16} />
              <Skeleton variant="rectangular" width={70} height={16} />
            </Box>
          </div>

          {/* Heatmap skeleton */}
          <Box sx={{ padding: 3 }}>
            {/* Month headers skeleton */}
            <Box sx={{ display: 'flex', gap: 1, marginBottom: 2, justifyContent: 'center' }}>
              {[...Array(12)].map((_, i) => (
                <Skeleton key={i} variant="text" width={30} height={20} />
              ))}
            </Box>

            {/* Heatmap rows skeleton */}
            {[...Array(8)].map((_, rowIndex) => (
              <Box key={rowIndex} sx={{ display: 'flex', gap: 1, marginBottom: 1, alignItems: 'center' }}>
                {/* Account label skeleton */}
                <Skeleton variant="text" width={200} height={30} />

                {/* Heatmap cells skeleton */}
                {[...Array(12)].map((_, colIndex) => (
                  <Skeleton
                    key={colIndex}
                    variant="rectangular"
                    width={30}
                    height={30}
                    sx={{ borderRadius: 0.5 }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </div>
      </Container>
    );
  }

  if (missingStatementsResult.error) {
    return (
      <div className={`${styles.stateBlock} ${styles.stateError}`}>
        {missingStatementsResult.error}
      </div>
    );
  }

  const hasData = data && Object.keys(data).length > 0;

  // flexGrow only while there's a scrolling chart to fill the viewport with —
  // an empty state has no reason to claim all remaining vertical space, which
  // otherwise leaves a large blank gap between this card and Detail below it.
  return (
    <Container maxWidth="xl" sx={{ flexGrow: hasData ? 1 : 0, padding: 2 }}>
      <div className={styles.card}>
        {/*
          Section title and key on one row. This was a centered title in a
          bordered box above a fixed 320x120 SVG legend, which cost ~250px of
          vertical band before any data and left most of that band empty.
          The key is a key — it belongs beside the heading, not in the middle
          of the card. Marked up as a list so it reads as one to a screen
          reader, and in HTML rather than SVG so it reflows instead of
          overflowing a fixed viewBox.
        */}
        <div className={styles.cardHeader}>
          {/* h2: the report's single h1 is its PageHeader. Standalone use of
              this component sits under whatever page title hosts it. */}
          <h2 className={styles.sectionTitle}>Summary Timeline</h2>

          <ul className={styles.legend} aria-label="Coverage key">
            <li className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.swatchOk}`} aria-hidden="true" />
              OK
            </li>
            <li className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.swatchWarning}`} aria-hidden="true" />
              Warning
            </li>
            <li className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.swatchReview}`} aria-hidden="true" />
              Needs review
            </li>
            <li className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles.swatchMissing}`} aria-hidden="true" />
              Missing
            </li>
            <li className={styles.legendItem} title="A cell is filled for the exact portion of the month the statement covers">
              <span className={`${styles.swatch} ${styles.swatchPartial}`} aria-hidden="true" />
              Partial month
            </li>
          </ul>
        </div>

        {toolbar}

        {hasData ? (
          <QualityTimeline
            data={data}
            visibleYears={visibleYears}
            collapsedGroups={collapsedGroups}
            onToggleGroup={onToggleGroup}
            stickyOffset={stickyOffset}
            onStickyTopChange={onStickyTopChange}
          />
        ) : (
          <div className={styles.stateBlock}>{emptyMessage}</div>
        )}
      </div>
    </Container>
  );
};

export default BankStatementQualityHeatmap;
